/**
 * Sérialisation des données signées — CONFORMITE-NF525 §2.3.
 * L'ORDRE DES CHAMPS EST FIGÉ (« ne jamais réordonner » ) : un ajout se fait EN FIN de chaîne, et tout
 * changement est un FORMAT nouveau (montée de version d'algorithme, ENC-TIK-TAG-VER). Une pièce garde à
 * jamais le format sous lequel elle a été signée (`PieceFormat`).
 * Conventions transverses :
 *  - séparateur de champs `|`, listes `;`, sous-champs `:` — INTERDITS dans les valeurs (assertion) ;
 *  - montants en CENTIMES ENTIERS signés (retours négatifs) ;
 *  - date/heure locale `AAAAMMJJHHMMSS` fournie par l'HEURE DE CONFIANCE du produit (§30.6 —
 *    la lib ne lit JAMAIS l'horloge elle-même) ;
 *  - champ vide = chaîne vide '' (jamais 'null') ;
 *  - encodage UTF-8 au hachage (sha256Hex).
 */

/** Version de l'algorithme de sérialisation (R13 ENC-TIK-TAG-VER) — bump si ajout de champ.
 *  v2 (2026-07-18) : les GRANDS TOTAUX gagnent les totaux de PÉRIODE (R13 ENC-GTP-TTC/HT obligatoires
 *  + ventilation) et se chaînent PAR PÉRIODICITÉ (R13 [6] : « la signature précédente est celle du
 *  grand total précédent pour la même périodicité » — mars se chaîne à février, jamais à un GT d'un
 *  autre type).
 *  v3 (2026-07-22, TOUJOURS avant toute pièce de production) : la PIÈCE gagne l'IDENTITÉ DE
 *  L'ÉMETTEUR (8 zones R13 `ENC-TIK-SOC-*`), la VERSION LOGICIELLE (`ENC-TIK-TAG-VER`) et les
 *  3 zones CLIENT de R12 (`TAG-FAC-CLI-NOM/-CPP/-TVA`, obligatoires pour facture/avoir).
 *  POURQUOI dans la SIGNATURE et pas seulement en colonnes : le §6.3.1.1 exige une restitution
 *  « strictement identique » sur les duplicatas. Un snapshot en base protégé par un trigger reste
 *  altérable par qui atteint le fichier ; scellé, il ne l'est plus. La ventilation TVA porte en
 *  outre désormais le CODE TVA en tête de chaque bloc (`code:pb:HT:TVA`, core-vat).
 *  v4 (2026-07-31, TOUJOURS avant toute pièce de production) : la PIÈCE gagne le NOM (désignation) de
 *  l'OPÉRATEUR de saisie (R13 `ENC-TIK-OPS-NOM`) en SNAPSHOT signé, à côté de son code (`operateurId` =
 *  `ENC-TIK-OPS-NID`). POURQUOI signé et pas seulement `operator_id` → `operator.name` : la table opérateur
 *  est MUTABLE (renommage) et effaçable (RGPD) ; sans snapshot scellé, un renommage futur réécrirait le nom
 *  affiché sur les pièces/duplicatas historiques (§6.3.1.1 restitution identique). Champ inséré EN FIN, avant
 *  la signature précédente (élément terminal du chaînage). Même fenêtre que v3 : aucune chaîne en production.
 *  v5 (2026-10-27, avant toute pièce de production) : la PIÈCE NE SIGNE PLUS SES RÈGLEMENTS (décision
 *  utilisateur). Ni R13 (ticket) ni R12 (facture) ne les comptent dans la signature — ils les y ajoutaient
 *  au-delà du minimum. Signés, un changement de règlement ne pouvait que contredire la signature ; hors
 *  signature, il ajoute ses lignes « moins »/« plus » au ticket et le JET 420 garde le règlement de départ.
 *  ⚠️ Des pièces v4 existent en bêta : elles restent valides et se re-sérialisent au format 4
 *  (`pieceFormatOf`). Cette fenêtre se referme à la première vente réelle. */
export const SERIALIZATION_VERSION = '5';

/**
 * LES FORMATS DE PIÈCE QUI EXISTENT DANS DES CHAÎNES SIGNÉES.
 * ⚠️ Un vérificateur qui RE-SÉRIALISE une pièce depuis ses colonnes doit appliquer le format de CETTE pièce, jamais
 *    celui du jour : une pièce v4 relue au format 5 serait déclarée divergente alors qu'elle est intacte.
 */
export type PieceFormat = '4' | '5';

/** Le nombre de champs de chaque format — c'est à lui qu'une chaîne signée dit son format. */
export const PIECE_FIELD_COUNT: Readonly<Record<PieceFormat, number>> = { '4': 23, '5': 22 };

/**
 * Le format d'une chaîne de pièce signée, lu dans la chaîne elle-même.
 * ⚠️ PAR SON NOMBRE DE CHAMPS, et c'est sûr : le séparateur `|` est interdit dans toute valeur (`assertFieldSafe`,
 *    `formatPaiements`), donc deux formats de longueurs différentes ne se confondent jamais. Aucune colonne de plus, et
 *    rien qu'un auditeur ne puisse refaire avec la chaîne seule. `null` : ce n'est la chaîne d'aucun format connu.
 */
export function pieceFormatOf(signedPayload: string): PieceFormat | null {
  const n = signedPayload.split('|').length;
  for (const f of Object.keys(PIECE_FIELD_COUNT) as PieceFormat[]) if (PIECE_FIELD_COUNT[f] === n) return f;
  return null;
}

import { isInvoiceSeriesNumber } from './sequence';

export type PieceType = 'ticket' | 'facture' | 'note' | 'avoir' | 'titre' | 'justificatif';
/** Type d'opération de la pièce — R13 `ENC-OPE-TYP` / jeton signé `TAG-TIK-OPE-TYP`.
 *  `changement_reglement` (ajout 2026-07, §7.1) : pièce corrective de re-ventilation des moyens de
 *  paiement (total 0, règlements −anciens/+nouveaux) — valeur ADDITIVE, la sérialisation v1 est inchangée. */
export type OperationType = 'vente' | 'retour_partiel' | 'retour_total' | 'echange' | 'annulation' | 'avoir' | 'changement_reglement'
  /** Opération de TRÉSORERIE (§ 6.18.1) : l'émission d'un titre — chèque cadeau, acompte. Encaissée, signée, chaînée, mais
   *  HORS grands totaux (`countsInGrandTotals`). Valeur ADDITIVE, comme `changement_reglement` : la sérialisation ne change
   *  pas de forme. */
  | 'tresorerie';
/** GTT = Grand Total Ticket (photographie à chaque pièce, R13 ENC-GTT-*) ; GTJ/GTM/GTA = périodiques (ENC-GTP-*).
 *  `GTS` (ajout 2026-07, §9.4bis) = clôture de SESSION de caisse (« ticket Z », plusieurs par journée
 *  fiscale — 2 sessions = 2 Z §9.1ter) : photographie signée des cumuls à la fermeture d'une session,
 *  `periodeId` = n° séquentiel du Z (`Z-S…R…-NNNNNN`, précédent GTT = n° de pièce). Valeur ADDITIVE,
 *  sérialisation v1 inchangée — R13 [3] : le grand-total périodique est constitué « A MINIMA » du
 *  GTJ/GTM/GTA, des clôtures SUPPLÉMENTAIRES sont permises ; le GTJ reste l'unique clôture JOURNALIÈRE. */
export type GrandTotalType = 'GTT' | 'GTJ' | 'GTM' | 'GTA' | 'GTS';

/** Vérifie qu'une valeur ne contient aucun séparateur réservé ni saut de ligne */
export function assertFieldSafe(value: string, field: string): string {
  if (/[|\n\r]/.test(value)) throw new Error(`core-nf525: caractère réservé dans « ${field} » : ${JSON.stringify(value)}`);
  return value;
}

/**
 * UN TEXTE LIBRE RENDU SIGNABLE — les retours à la ligne et la barre verticale, séparateurs de la charge signée, deviennent
 * « / ». Pour ce qu'un opérateur saisit ou qu'une erreur compose (un motif, le descriptif d'un JET) : refusé APRÈS
 * l'opération qu'il trace, il faisait perdre la trace, pas le caractère.
 * ⚠️ LE TEXTE RENDU EST CELUI QU'ON STOCKE : appelée une fois, à l'entrée, son résultat sert à la signature ET à la colonne
 *    — deux textes différents rendraient la charge signée invérifiable depuis la base.
 */
export function toSignableText(text: string): string {
  return text.replace(/\s*(?:\r\n|\r|\n)+\s*/g, ' / ').replace(/\|/g, '/').trim();
}

/** Formate une Date LOCALE en AAAAMMJJHHMMSS (l'heure de confiance reste côté produit, §30.6) */
export function formatDateTime(d: Date): string {
  const p = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
}

const DT_RE = /^\d{14}$/;
export function assertDateTime(dt: string): string {
  if (!DT_RE.test(dt)) throw new Error(`core-nf525: dateHeure attendue au format AAAAMMJJHHMMSS (reçu ${JSON.stringify(dt)})`);
  return dt;
}

export interface PaymentLine {
  /** Code moyen de paiement (ex. CB, ESP, CHQ, TITRE, WALLET) — sans `|;:` */
  type: string;
  montantCents: number;
}

/** Liste des paiements → `type:montant;type:montant` (ordre de saisie, montants centimes) */
export function formatPaiements(paiements: PaymentLine[]): string {
  return paiements
    .map(p => {
      if (!Number.isInteger(p.montantCents)) throw new Error('core-nf525: montant paiement en centimes entiers attendu');
      if (/[|;:\n\r]/.test(p.type)) throw new Error(`core-nf525: type de paiement invalide ${JSON.stringify(p.type)}`);
      return `${p.type}:${p.montantCents}`;
    })
    .join(';');
}

/**
 * Identité de l'ÉMETTEUR au moment de la pièce — 8 zones R13 `ENC-TIK-SOC-*`, cotées X en Données,
 * Archives ET impression. SNAPSHOT : recopiée sur CHAQUE pièce et scellée avec elle.
 * ⚠️ Ne JAMAIS la relire dans la configuration courante au moment d'un duplicata : la config est
 * mutable, et le §6.3.1.1 impose que le duplicata restitue l'identité de l'ORIGINE. Un magasin qui
 * change de raison sociale ne doit pas réécrire l'histoire de ses tickets.
 */
export interface EmitterIdentity {
  raisonSociale: string;
  adresse: string;
  codePostal: string;
  ville: string;
  pays: string;
  siret: string;
  naf: string;
  tvaIntra: string;
}

/**
 * Identité du CLIENT — zones R12 `TAG-FAC-CLI-NOM` / `-CPP` / `-TVA`, OBLIGATOIRES dans la charge
 * signée des FACTURES, factures d'acompte, AVOIRS et proformas (§6.11.3.6). Vide pour un ticket :
 * R13 ne les réclame pas, mais on conserve une forme de chaîne UNIQUE — une variante de sérialisation
 * par type de pièce multiplierait les chemins de vérification, donc les façons de se tromper.
 */
export interface ClientIdentity {
  nom: string;
  codePostal: string;
  tvaIntra: string;
}

export const EMPTY_CLIENT: ClientIdentity = Object.freeze({ nom: '', codePostal: '', tvaIntra: '' });

/** Données d'une pièce (ticket/facture/avoir…) à sceller — cf. CONFORMITE §2.3 (1) */
export interface PieceData {
  typePiece: PieceType;
  /** Type d'opération (vente/retour/annulation…) — entre dans la signature (TAG-TIK-OPE-TYP) */
  typeOperation: OperationType;
  /** Numéro complet de la pièce (ex. T-74220-1-000312) — séquence continue dédiée §30.7.1 */
  pieceNumber: string;
  dateHeure: string;
  registerId: string;
  operateurId: string;
  /** Désignation (NOM) de l'opérateur de saisie — SNAPSHOT SIGNÉ (v4), R13 `ENC-TIK-OPS-NOM`. Figé sur la
   *  pièce comme l'émetteur : un renommage/effacement futur de l'opérateur ne réécrit pas les pièces
   *  historiques (§6.3.1.1). `operateurId` = le CODE signé (`ENC-TIK-OPS-NID`), celui-ci = le NOM signé. */
  operateurNom: string;
  /** Total TTC en centimes, signé (retour/avoir négatif) */
  montantTtcCents: number;
  /** Ventilation TVA canonique de @orgazone/core-vat : `code:pb:HT:TVA;…` taux croissants (v3) */
  ventilationTva: string;
  /** Règlements — signés au SEUL format 4. Le format courant ne les signe pas : ils ne servent qu'à re-sérialiser une
   *  pièce v4 pour la vérifier. */
  paiements?: PaymentLine[];
  /** Identité de l'émetteur FIGÉE sur la pièce (v3) — R13 `ENC-TIK-SOC-*` */
  emetteur: EmitterIdentity;
  /** Version du logiciel qui a produit ET signé la pièce (v3) — R13 `ENC-TIK-TAG-VER` */
  versionLogiciel: string;
  /** Identité client (v3) — obligatoire pour les pièces R12 (facture/avoir) ; `EMPTY_CLIENT` sinon */
  client: ClientIdentity;
}

/**
 * Types de pièce relevant du dictionnaire R12 (FACTURES) : leurs zones CLIENT sont obligatoires
 * à la signature. ⚠️ `avoir` n'y figure PAS : chez nous ce type désigne d'abord le remboursement au
 * comptoir — une opération de CAISSE relevant de R13, sans client identifié. L'avoir que R12 vise
 * est l'avoir SUR FACTURE : numéroté dans la série des factures, il emprunte la même garde (`isR12Piece`).
 */
const R12_PIECES: ReadonlySet<PieceType> = new Set<PieceType>(['facture']);

/**
 * Cette pièce relève-t-elle du dictionnaire R12 — une facture, ou une facture d'avoir (un avoir numéroté dans la série
 * des factures) ?
 * ⚠️ LE NUMÉRO DÉCIDE, PAS UN DRAPEAU DE PLUS : la facture d'avoir est un `avoir` (code `AVO`, aucun vocabulaire signé
 *    neuf — Forme J) que seule sa série distingue de l'avoir de comptoir, et c'est la série qu'un auditeur lit.
 */
export function isR12Piece(typePiece: PieceType, pieceNumber: string): boolean {
  return R12_PIECES.has(typePiece) || (typePiece === 'avoir' && isInvoiceSeriesNumber(pieceNumber));
}

/**
 * Chaîne signée d'une PIÈCE (ordre EXACT CONFORMITE §2.3) — format 5, le courant :
 * typePiece|typeOperation|numeroPiece|dateHeure|caisseId|operateurId|montantTTC|ventilationTVA|
 * raisonSociale|adresse|CP|ville|pays|SIRET|NAF|TVAintra|versionLogiciel|
 * clientNom|clientCP|clientTVA|operateurNom|signaturePiecePrecedente
 * Le format 4 porte en plus `moyensPaiement` juste après la ventilation TVA.
 * (`typeOperation` couvre R13 TAG-TIK-OPE-TYP ; la signature précédente reste l'élément TERMINAL structurel du
 * chaînage — `verifyChain` exige que la chaîne se termine par elle.)
 *
 * ⚠️ Les zones CLIENT sont exigées VIDES ou remplies selon le type : pour une FACTURE ou un AVOIR,
 * R12 les rend obligatoires — on refuse de sceller une pièce R12 sans nom de client, plutôt que de
 * produire une signature conforme en apparence sur une zone requise laissée à blanc.
 * ⚠️ `format` ne sert qu'à RE-SÉRIALISER une pièce déjà signée (vérification) : on ne scelle qu'au format courant.
 */
export function serializePiece(p: PieceData, previousSignature: string, format: PieceFormat = SERIALIZATION_VERSION): string {
  if (!Number.isInteger(p.montantTtcCents)) throw new Error('core-nf525: montantTTC en centimes entiers attendu');
  if (isR12Piece(p.typePiece, p.pieceNumber) && p.client.nom.trim() === '') {
    throw new Error(`core-nf525: pièce ${p.typePiece} — le nom du client est OBLIGATOIRE dans la charge signée (R12 TAG-FAC-CLI-NOM)`);
  }
  if (format === '4' && !p.paiements) throw new Error('core-nf525: une pièce au format 4 signe ses règlements — `paiements` manquant');
  const e = p.emetteur;
  return [
    assertFieldSafe(p.typePiece, 'typePiece'),
    assertFieldSafe(p.typeOperation, 'typeOperation'),
    assertFieldSafe(p.pieceNumber, 'pieceNumber'),
    assertDateTime(p.dateHeure),
    assertFieldSafe(p.registerId, 'registerId'),
    assertFieldSafe(p.operateurId, 'operateurId'),
    String(p.montantTtcCents),
    assertFieldSafe(p.ventilationTva, 'ventilationTva'),
    ...(format === '4' ? [formatPaiements(p.paiements ?? [])] : []),
    assertFieldSafe(e.raisonSociale, 'emetteur.raisonSociale'),
    assertFieldSafe(e.adresse, 'emetteur.adresse'),
    assertFieldSafe(e.codePostal, 'emetteur.codePostal'),
    assertFieldSafe(e.ville, 'emetteur.ville'),
    assertFieldSafe(e.pays, 'emetteur.pays'),
    assertFieldSafe(e.siret, 'emetteur.siret'),
    assertFieldSafe(e.naf, 'emetteur.naf'),
    assertFieldSafe(e.tvaIntra, 'emetteur.tvaIntra'),
    assertFieldSafe(p.versionLogiciel, 'versionLogiciel'),
    assertFieldSafe(p.client.nom, 'client.nom'),
    assertFieldSafe(p.client.codePostal, 'client.codePostal'),
    assertFieldSafe(p.client.tvaIntra, 'client.tvaIntra'),
    assertFieldSafe(p.operateurNom, 'operateurNom'), // v4 — R13 ENC-TIK-OPS-NOM (snapshot signé du NOM)
    assertFieldSafe(previousSignature, 'signaturePrecedente'),
  ].join('|');
}

/** Données d'un DUPLICATA à sceller — R13 bloc `TAG-DUP-*` / R12 `FAC-DUP-SIG` (v3, 2026-07-22). */
export interface ReprintData {
  /** Numéro de la pièce réimprimée (R13 `ENC-DUP-ORI-NUM`) */
  originPieceNumber: string;
  /** Rang du duplicata : 1, 2, 3… (`ENC-DUP-NUM`) */
  rang: number;
  /** Canal d'émission (ticket | a4 | email | gift) */
  canal: string;
  /** Motif d'émission (`TAG-DUP-ORI-MOT`) — OBLIGATOIRE : il entre dans la chaîne signée */
  motif: string;
  dateHeure: string;
  registerId: string;
  operateurId: string;
  /** Version du logiciel émettant le duplicata (R13 `ENC-DUP-VER`) */
  versionLogiciel: string;
}

/**
 * Chaîne signée d'un DUPLICATA (§2.3, v3) :
 * numeroPieceOrigine|rang|canal|motif|dateHeure|caisseId|operateurId|versionLogiciel|signatureDuplicataPrecedent
 *
 * POURQUOI une 4ᵉ chaîne : le §6.11.3.6 liste explicitement « duplicata » parmi les objets soumis à
 * signature, au même rang que les ventes, les Grands Totaux et le JET. Journaliser l'émission d'un
 * duplicata (JET 155) et SIGNER le duplicata lui-même sont deux obligations distinctes — la première
 * était satisfaite, pas la seconde.
 */
export function serializeReprint(r: ReprintData, previousSignature: string): string {
  if (!Number.isInteger(r.rang) || r.rang < 1) throw new Error('core-nf525: rang de duplicata entier ≥ 1 attendu');
  if (r.motif.trim() === '') {
    throw new Error('core-nf525: motif de duplicata OBLIGATOIRE (R13 TAG-DUP-ORI-MOT entre dans la chaîne signée)');
  }
  return [
    assertFieldSafe(r.originPieceNumber, 'originPieceNumber'),
    String(r.rang),
    assertFieldSafe(r.canal, 'canal'),
    assertFieldSafe(r.motif, 'motif'),
    assertDateTime(r.dateHeure),
    assertFieldSafe(r.registerId, 'registerId'),
    assertFieldSafe(r.operateurId, 'operateurId'),
    assertFieldSafe(r.versionLogiciel, 'versionLogiciel'),
    assertFieldSafe(previousSignature, 'signatureDuplicataPrecedent'),
  ].join('|');
}

/** Données d'un événement JET à sceller — cf. CONFORMITE §2.3 (2) */
export interface JetData {
  /** Code événement R19 (cf. jet.ts) */
  codeEvenement: number;
  dateHeure: string;
  registerId: string;
  operateurId: string;
  /** Descriptif libre (sans `|`) */
  descriptif: string;
}

/**
 * Chaîne signée d'une LIGNE JET (ordre EXACT CONFORMITE §2.3) :
 * codeEvenement|dateHeure|caisseId|operateurId|descriptif|compteurLigne|signaturePrecedente
 */
export function serializeJet(j: JetData, compteurLigne: number, previousSignature: string): string {
  if (!Number.isInteger(j.codeEvenement) || !Number.isInteger(compteurLigne)) {
    throw new Error('core-nf525: codeEvenement/compteurLigne entiers attendus');
  }
  return [
    String(j.codeEvenement),
    assertDateTime(j.dateHeure),
    assertFieldSafe(j.registerId, 'registerId'),
    assertFieldSafe(j.operateurId, 'operateurId'),
    assertFieldSafe(j.descriptif, 'descriptif'),
    String(compteurLigne),
    assertFieldSafe(previousSignature, 'signaturePrecedente'),
  ].join('|');
}

/** Données d'un Grand Total à sceller — cf. CONFORMITE §2.3 (3) */
export interface GrandTotalData {
  typeGT: GrandTotalType;
  /** Identifiant DATÉ non équivoque (R13 ENC-GTP-ORI-NID) : J-AAAAMMJJ / M-AAAAMM / E-AAAAMMJJ-AAAAMMJJ ; pour un GTT : le n° de la pièce (ENC-GTT-ORI-NUM) ; pour un GTS : le n° du Z (Z-S…R…) */
  periodeId: string;
  registerId: string;
  dateHeure: string;
  /** GTPCA : cumul TTC perpétuel ALGÉBRIQUE (centimes, jamais remis à zéro) — ENC-GTP-PER-TTC */
  cumulTtcCents: number;
  /** GTPVA : cumul TTC perpétuel en VALEUR ABSOLUE — ENC-GTP-PER-TTC-ABS (anti-compensation, signé lui aussi) */
  cumulTtcAbsCents: number;
  /** Cumul perpétuel par taux, format canonique `pb:HT:TVA;…` taux croissants */
  cumulParTaux: string;
  /** Compteur perpétuel de pièces scellées */
  compteurPieces: number;
  /** GT TTC de la PÉRIODE close (R13 ENC-GTP-TTC, obligatoire) — delta vs clôture précédente de même périodicité (v2) */
  periodeTtcCents: number;
  /** GT HT de la PÉRIODE close (R13 ENC-GTP-HT, obligatoire) (v2) */
  periodeHtCents: number;
  /** Ventilation de la PÉRIODE, canonique `pb:HT:TVA;…` (taux au delta nul omis) (v2) */
  periodeParTaux: string;
}

/**
 * Chaîne signée d'un GRAND TOTAL (ordre EXACT CONFORMITE §2.3, v2 2026-07-18 — champs v2 insérés
 * AVANT la signature précédente, élément terminal structurel du chaînage) :
 * typeGT|periodeId|caisseId|dateHeure|cumulTTC|cumulTTCAbs|cumulParTaux|compteurPieces|periodeTTC|periodeHT|periodeParTaux|signatureGTPrecedent
 * ⚠️ `signatureGTPrecedent` = celle du GT précédent de MÊME PÉRIODICITÉ (R13 [6] — mars → février),
 * fournie par l'engine (chaînes par type). v1 (sans les 3 champs periode*) : plus jamais émise —
 * bump AVANT toute donnée client, aucune chaîne v1 en production.
 */
export function serializeGrandTotal(g: GrandTotalData, previousSignature: string): string {
  if (!Number.isInteger(g.cumulTtcCents) || !Number.isInteger(g.cumulTtcAbsCents) || !Number.isInteger(g.compteurPieces)) {
    throw new Error('core-nf525: cumuls GT en entiers attendus');
  }
  if (!Number.isInteger(g.periodeTtcCents) || !Number.isInteger(g.periodeHtCents)) {
    throw new Error('core-nf525: totaux de période GT en entiers attendus');
  }
  return [
    assertFieldSafe(g.typeGT, 'typeGT'),
    assertFieldSafe(g.periodeId, 'periodeId'),
    assertFieldSafe(g.registerId, 'registerId'),
    assertDateTime(g.dateHeure),
    String(g.cumulTtcCents),
    String(g.cumulTtcAbsCents),
    assertFieldSafe(g.cumulParTaux, 'cumulParTaux'),
    String(g.compteurPieces),
    String(g.periodeTtcCents),
    String(g.periodeHtCents),
    assertFieldSafe(g.periodeParTaux, 'periodeParTaux'),
    assertFieldSafe(previousSignature, 'signatureGTPrecedent'),
  ].join('|');
}
